<script setup lang="ts">
import { useGameStore } from '@/stores/gameStore.ts'
import { useAssetStore } from '@/stores/assetStore.ts'
import { useAllianceStore } from '@/stores/allianceStore.ts'
import { computed } from 'vue'
import Backend from '@/models/backend.ts'
import { CDN } from '@/models/cdn.ts'
import LargeWideModel from '@/components/Interface/Modal/ModelSizes/LargeWideModel.vue'
import StandardCard from '@/components/Elements/Cards/StandardCard.vue'
import NamedNumbers from '@/components/Elements/Generic/NamedNumbers.vue'
import GenericIconWithNumber from '@/components/Elements/Images/Icons/GenericIconWithNumber.vue'
import ModalTechBuilding from '@/components/Interface/Modal/Modals/ModalTech/ModalTechBuilding.vue'
import ModalTechShipComponent from '@/components/Interface/Modal/Modals/ModalTech/ModalTechShipComponent.vue'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

/**
 * Stores / models
 */
const gameStore = useGameStore()
const assetStore = useAssetStore()
const allianceStore = useAllianceStore()
const router = useRouter()

/**
 * Computed
 */
const techData = computed(() => assetStore.findTechById(gameStore.modalData.techId)!)

// Get current alliance
const currentAlliance = computed(() => {
  return gameStore.player?.allianceId ? allianceStore.getAlliance(gameStore.player.allianceId) : null
})

const isAlreadyPrototyped = computed(() => {
  // Check if this prototype has already been researched by the alliance
  return currentAlliance.value?.prototypeIds?.includes(gameStore.modalData.techId) || false
})

const showPrototypeButton = computed(() => {
  return !isAlreadyPrototyped.value
})

const buildings = computed(() => {
  const buildings = []
  for (const benefit of techData.value.provides) {
    if (benefit.type === 'buildings') {
      buildings.push(benefit.code)
    }
  }
  return buildings
})

const shipComponents = computed(() => {
  const shipComponents = []
  for (const benefit of techData.value.provides) {
    if (benefit.type === 'ship_components') {
      shipComponents.push(benefit.code)
    }
  }
  return shipComponents
})


/**
 * Functions
 */
const selectPrototype = async () => {
  try {
    // Set alliance prototype via API call
    const backend = new Backend()
    await backend.setAlliancePrototype(gameStore.modalData.techId)

    gameStore.setTinySuccessToastMessage(t('modals.allianceTech.messages.selectedPrototype', { name: t(`research.techs.${techData.value.code}.name`) }))

    // Close modal and reset alliance prototype selection
    gameStore.modalData = []
    gameStore.modalType = ''
    gameStore.resetAlliancePrototypeSelection()
    await router.push('/diplomacy/alliance/research')
  } catch (error) {
    gameStore.setTinyErrorToastMessage(t('modals.allianceTech.messages.selectFailed'))
  }
}
</script>

<template>
  <LargeWideModel :title="t(`research.techs.${techData.code}.name`)">
    <div class="p-2 lg:flex">
      <div class="w-full lg:w-1/2">
        <div class="flex flex-col md:flex-row">
          <img
            :src="CDN.technology(256, techData.code)"
            alt="Technology"
            class="shrink-0 rounded w-auto h-auto md:w-64 md:h-64 object-cover"
          />
          <div class="md:ps-2 pt-2 md:pt-0 w-full">
            <StandardCard :title="t(`research.techs.${techData.code}.name`)" top-padding="0">
              <p>{{ t(`research.techs.${techData.code}.description`) }}</p>
              <div class="pt-2 flex justify-between items-center">
                <NamedNumbers :text="t('modals.allianceTech.scienceCost')"
                  ><GenericIconWithNumber :amount="techData.cost" type="science"
                /></NamedNumbers>
                <div>
                  <ActionButton v-if="showPrototypeButton" @click="selectPrototype">
                    <GenericIconWithNumber
                      :amount="techData.cost"
                      type="blueprint"
                      text-color="text-slate-300"
                    />
                  </ActionButton>
                  <div v-else class="text-blue-400 text-sm">
                    {{ t('modals.allianceTech.alreadyPrototyped') }}
                  </div>
                </div>
              </div>
            </StandardCard>
          </div>
        </div>
      </div>
      <div class="w-full lg:w-1/2 ps-0 lg:ps-2 pt-2 lg:pt-0">
        <template v-for="buildingCode in buildings" :key="buildingCode">
          <ModalTechBuilding :buildingCode="buildingCode" />
        </template>
        <template v-for="shipComponentCode in shipComponents" :key="shipComponentCode">
          <ModalTechShipComponent :shipComponentCode="shipComponentCode" />
        </template>
      </div>
    </div>
  </LargeWideModel>
</template>

<style scoped lang="scss">
.main-image {
  width: 15rem;
  height: 15rem;
}
</style>
